import React, { FC, useCallback, useMemo, useState } from "react";
import Chip from "@mui/material/Chip";
import Button from "@mui/material/Button";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import ListSubheader from "@mui/material/ListSubheader";
import Tooltip from "@mui/material/Tooltip";
import Checkbox from "@mui/material/Checkbox";
import TextField from "@mui/material/TextField";
import InputAdornment from "@mui/material/InputAdornment";
import SearchIcon from "@mui/icons-material/Search";
import ClearIcon from "@mui/icons-material/Clear";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import FilterListIcon from "@mui/icons-material/FilterList";
import { formatLabel } from "utils/formatLabel";
import { LEADS_FILTER_MAP } from "constants/dashboardFilters";

/** Filter types that can be applied from chart interactions */
export type DashboardFilterType =
    | 'source' | 'referringSource' | 'contactType' | 'serviceType' | 'jobType' | 'urgency'
    | 'highValueLead' | 'zipCode'
    // Lead taxonomy — the fields the taxonomy pies aggregate on
    | 'contactMethod' | 'agentType' | 'capturePlatform' | 'sourceSystem';

/** Filter value with metadata */
export interface DashboardFilterValue {
    filterType: DashboardFilterType;
    value: string;
    displayName: string;
}

/** Active filters state */
export interface DashboardActiveFilters {
    source?: string;
    referringSource?: string;
    contactType?: string;
    serviceType?: string;
    jobType?: string;
    urgency?: string;
    highValueLead?: boolean;
    zipCode?: string;
    contactMethod?: string;
    agentType?: string;
    capturePlatform?: string;
    sourceSystem?: string;
}

/** Available filter options for the Add Filter menu */
export interface AvailableFilterOptions {
    sources?: string[];
    referringSources?: string[];
    contactTypes?: string[];
    serviceTypes?: string[];
    jobTypes?: string[];
    urgencies?: string[];
}

/** Human-readable labels for filter types */
const FILTER_TYPE_LABELS: Record<DashboardFilterType, string> = {
    source: 'Channel',
    referringSource: 'Referring Source',
    contactType: 'Contact Type',
    serviceType: 'Service Type',
    jobType: 'Job Type',
    urgency: 'Urgency',
    highValueLead: 'Lead Type',
    zipCode: 'Zip Code',
    contactMethod: 'Contact Method',
    agentType: 'Agent Type',
    capturePlatform: 'Capture Platform',
    sourceSystem: 'Source System'
};

/** Maximum number of job types to display in the filter menu */
const MAX_JOB_TYPES_DISPLAYED = 10;

/** Default container styles when no className is provided */
const defaultContainerStyle: React.CSSProperties = {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    padding: '8px 0',
    flexWrap: 'wrap',
    minHeight: '40px'
};

export interface DashboardFilterBarProps {
    /** Total number of items (used for disabled state when 0) */
    totalItems?: number;
    /** Currently active filters */
    activeFilters?: DashboardActiveFilters;
    /** Available filter options for the Add Filter menu */
    availableFilterOptions?: AvailableFilterOptions;
    /** Callback when user selects a filter */
    onFilterApply?: (filter: DashboardFilterValue) => void;
    /** Callback to clear a specific filter */
    onFilterClear?: (filterType: DashboardFilterType) => void;
    /** Callback to clear all filters */
    onClearAllFilters?: () => void;
    /** Callback when user clicks "View Lead Details" chip */
    onViewLeads?: () => void;
    /** Whether to show the organization filter in the Add Filter menu */
    showOrganizationFilter?: boolean;
    /** Available organizations for the organization filter dropdown */
    availableOrganizations?: Array<{ organizationId: string; name: string }>;
    /** Currently selected organization IDs for filtering */
    selectedOrganizationIds?: string[];
    /** Callback when organization filter selection changes */
    onOrganizationFilterChange?: (organizationIds: string[]) => void;
    /** Optional CSS class name for the container */
    className?: string;
}

export const DashboardFilterBar: FC<DashboardFilterBarProps> = ({
    totalItems = 0,
    activeFilters: activeFiltersProp,
    availableFilterOptions,
    onFilterApply,
    onFilterClear,
    onClearAllFilters,
    onViewLeads,
    showOrganizationFilter = false,
    availableOrganizations = [],
    selectedOrganizationIds = [],
    onOrganizationFilterChange,
    className,
}) => {
    const activeFilters = activeFiltersProp || {};

    // Menu state
    const [filterMenuAnchor, setFilterMenuAnchor] = useState<null | HTMLElement>(null);
    const filterMenuOpen = Boolean(filterMenuAnchor);

    // Organization search state
    const [orgSearchQuery, setOrgSearchQuery] = useState('');

    // Build list of active filter chips
    const activeFilterChips = useMemo(() => {
        const chips: Array<{ filterType: DashboardFilterType; label: string; value: string }> = [];

        if (activeFilters.source) {
            chips.push({ filterType: 'source', label: FILTER_TYPE_LABELS.source, value: formatLabel(activeFilters.source) });
        }
        if (activeFilters.referringSource) {
            chips.push({ filterType: 'referringSource', label: FILTER_TYPE_LABELS.referringSource, value: formatLabel(activeFilters.referringSource) });
        }
        if (activeFilters.contactType) {
            chips.push({ filterType: 'contactType', label: FILTER_TYPE_LABELS.contactType, value: formatLabel(activeFilters.contactType) });
        }
        if (activeFilters.serviceType) {
            chips.push({ filterType: 'serviceType', label: FILTER_TYPE_LABELS.serviceType, value: formatLabel(activeFilters.serviceType) });
        }
        if (activeFilters.jobType) {
            chips.push({ filterType: 'jobType', label: FILTER_TYPE_LABELS.jobType, value: formatLabel(activeFilters.jobType) });
        }
        if (activeFilters.urgency) {
            chips.push({ filterType: 'urgency', label: FILTER_TYPE_LABELS.urgency, value: formatLabel(activeFilters.urgency) });
        }
        if (activeFilters.highValueLead !== undefined) {
            chips.push({ filterType: 'highValueLead', label: FILTER_TYPE_LABELS.highValueLead, value: activeFilters.highValueLead ? 'High Value' : 'Regular' });
        }
        if (activeFilters.zipCode) {
            chips.push({ filterType: 'zipCode', label: FILTER_TYPE_LABELS.zipCode, value: activeFilters.zipCode });
        }
        // Lead taxonomy. A filter with no chip is a dashboard that is filtered with nothing on
        // screen saying so — and since "Clear All" and "View Lead Details" are gated on the chip
        // count, the URL would be the only way back out.
        if (activeFilters.contactMethod) {
            chips.push({ filterType: 'contactMethod', label: FILTER_TYPE_LABELS.contactMethod, value: formatLabel(activeFilters.contactMethod) });
        }
        if (activeFilters.agentType) {
            chips.push({ filterType: 'agentType', label: FILTER_TYPE_LABELS.agentType, value: formatLabel(activeFilters.agentType) });
        }
        if (activeFilters.capturePlatform) {
            chips.push({ filterType: 'capturePlatform', label: FILTER_TYPE_LABELS.capturePlatform, value: formatLabel(activeFilters.capturePlatform) });
        }
        if (activeFilters.sourceSystem) {
            chips.push({ filterType: 'sourceSystem', label: FILTER_TYPE_LABELS.sourceSystem, value: formatLabel(activeFilters.sourceSystem) });
        }

        return chips;
    }, [activeFilters]);

    // "View Lead Details" hands the active filters to the leads list, which reads only the params in
    // LEADS_FILTER_MAP. Offering it for a filter that map doesn't carry — the taxonomy filters today —
    // would land the user on a list that silently ignores it, with a count contradicting the
    // dashboard they just filtered. Clear All is not gated this way: it can clear anything.
    const hasFilterTheLeadsListCanHonor = useMemo(
        () => activeFilterChips.some((chip) => !!LEADS_FILTER_MAP[chip.filterType]),
        [activeFilterChips]
    );

    // Derive available filter options
    const derivedFilterOptions = useMemo(() => {
        const options = availableFilterOptions || {};
        return {
            sources: options.sources || [],
            referringSources: options.referringSources || [],
            contactTypes: options.contactTypes || [],
            serviceTypes: options.serviceTypes || [],
            jobTypes: options.jobTypes || [],
            urgencies: options.urgencies || []
        };
    }, [availableFilterOptions]);

    // Filter available organizations by search query
    const filteredOrganizations = useMemo(() => {
        if (!orgSearchQuery.trim()) return availableOrganizations;
        const query = orgSearchQuery.toLowerCase();
        return availableOrganizations.filter(org =>
            org.name.toLowerCase().includes(query) ||
            org.organizationId.toLowerCase().includes(query)
        );
    }, [availableOrganizations, orgSearchQuery]);

    const handleAddFilterClick = useCallback((event: React.MouseEvent<HTMLButtonElement>) => {
        setFilterMenuAnchor(event.currentTarget);
    }, []);

    const handleFilterMenuClose = useCallback(() => {
        setFilterMenuAnchor(null);
        setOrgSearchQuery('');
    }, []);

    const handleOrgToggle = useCallback((orgId: string) => {
        if (!onOrganizationFilterChange) return;
        const newSelection = selectedOrganizationIds.includes(orgId)
            ? selectedOrganizationIds.filter(id => id !== orgId)
            : [...selectedOrganizationIds, orgId];
        onOrganizationFilterChange(newSelection);
    }, [selectedOrganizationIds, onOrganizationFilterChange]);

    const handleFilterSelect = useCallback((filterType: DashboardFilterType, value: string, displayName: string) => {
        setFilterMenuAnchor(null);
        onFilterApply?.({
            filterType,
            value,
            displayName
        });
    }, [onFilterApply]);

    const isFilterActive = (filterType: DashboardFilterType) => {
        return activeFilters[filterType] !== undefined;
    };

    const hasAvailableFilters =
        (derivedFilterOptions.sources.length > 0 && !isFilterActive('source')) ||
        (derivedFilterOptions.contactTypes.length > 0 && !isFilterActive('contactType')) ||
        (derivedFilterOptions.referringSources.length > 0 && !isFilterActive('referringSource')) ||
        (derivedFilterOptions.urgencies.length > 0 && !isFilterActive('urgency')) ||
        (derivedFilterOptions.serviceTypes.length > 0 && !isFilterActive('serviceType')) ||
        (derivedFilterOptions.jobTypes.length > 0 && !isFilterActive('jobType')) ||
        !isFilterActive('highValueLead');

    const getTooltipMessage = () => {
        if (totalItems === 0) {
            return "No leads available for the selected date range";
        }
        if (!hasAvailableFilters) {
            return "All available filters are already applied";
        }
        return "";
    };

    const tooltipMessage = getTooltipMessage();
    const isAddFilterDisabled = !hasAvailableFilters || totalItems === 0;

    return (
        <div className={className} style={!className ? defaultContainerStyle : undefined}>
            {/* Add Filter Button */}
            {onFilterApply && (
                <>
                    <Tooltip title={tooltipMessage} arrow disableHoverListener={!isAddFilterDisabled}>
                        <span>
                            <Button
                                variant="outlined"
                                size="small"
                                startIcon={<FilterListIcon />}
                                onClick={handleAddFilterClick}
                                disabled={isAddFilterDisabled}
                                sx={{
                                    textTransform: 'none',
                                    borderRadius: '16px',
                                    borderColor: '#1976d2',
                                    color: '#1976d2',
                                    '&:hover': {
                                        borderColor: '#1565c0',
                                        backgroundColor: 'rgba(25, 118, 210, 0.04)'
                                    },
                                    '&.Mui-disabled': {
                                        borderColor: '#ccc',
                                        color: '#999'
                                    }
                                }}
                            >
                                Add Filter
                            </Button>
                        </span>
                    </Tooltip>
                    <Menu
                        anchorEl={filterMenuAnchor}
                        open={filterMenuOpen}
                        onClose={handleFilterMenuClose}
                        anchorOrigin={{
                            vertical: 'bottom',
                            horizontal: 'left',
                        }}
                        transformOrigin={{
                            vertical: 'top',
                            horizontal: 'left',
                        }}
                        PaperProps={{
                            sx: {
                                maxHeight: 400,
                                minWidth: 280
                            }
                        }}
                    >
                        {/* Organization Filters - only on global views */}
                        {showOrganizationFilter && availableOrganizations.length > 0 && (
                            <>
                                <ListSubheader sx={{ lineHeight: '32px' }}>Organizations</ListSubheader>
                                <div style={{ padding: '0 16px 8px' }}>
                                    <TextField
                                        size="small"
                                        placeholder="Search organizations..."
                                        value={orgSearchQuery}
                                        onChange={(e) => setOrgSearchQuery(e.target.value)}
                                        onKeyDown={(e) => e.stopPropagation()}
                                        fullWidth
                                        InputProps={{
                                            startAdornment: (
                                                <InputAdornment position="start">
                                                    <SearchIcon sx={{ fontSize: 18, color: '#999' }} />
                                                </InputAdornment>
                                            ),
                                        }}
                                        sx={{ '& .MuiInputBase-input': { fontSize: 14 } }}
                                    />
                                </div>
                                <div style={{ maxHeight: 200, overflowY: 'auto' }}>
                                    {filteredOrganizations.map(org => (
                                        <MenuItem
                                            key={org.organizationId}
                                            onClick={() => handleOrgToggle(org.organizationId)}
                                            dense
                                            sx={{ pl: 2 }}
                                        >
                                            <Checkbox
                                                checked={selectedOrganizationIds.includes(org.organizationId)}
                                                size="small"
                                                sx={{ p: 0.5, mr: 1 }}
                                            />
                                            {org.name}
                                        </MenuItem>
                                    ))}
                                    {filteredOrganizations.length === 0 && (
                                        <MenuItem disabled sx={{ pl: 4, fontSize: 13, color: '#999' }}>
                                            No organizations found
                                        </MenuItem>
                                    )}
                                </div>
                            </>
                        )}

                        {/* Channel Filters */}
                        {derivedFilterOptions.sources.length > 0 && !isFilterActive('source') && (
                            <>
                                <ListSubheader>{FILTER_TYPE_LABELS.source}</ListSubheader>
                                {derivedFilterOptions.sources.map(value => (
                                    <MenuItem
                                        key={`source-${value}`}
                                        onClick={() => handleFilterSelect('source', value, formatLabel(value))}
                                        sx={{ pl: 4 }}
                                    >
                                        {formatLabel(value)}
                                    </MenuItem>
                                ))}
                            </>
                        )}

                        {/* Contact Type Filters */}
                        {derivedFilterOptions.contactTypes.length > 0 && !isFilterActive('contactType') && (
                            <>
                                <ListSubheader>{FILTER_TYPE_LABELS.contactType}</ListSubheader>
                                {derivedFilterOptions.contactTypes.map(value => (
                                    <MenuItem
                                        key={`contactType-${value}`}
                                        onClick={() => handleFilterSelect('contactType', value, formatLabel(value))}
                                        sx={{ pl: 4 }}
                                    >
                                        {formatLabel(value)}
                                    </MenuItem>
                                ))}
                            </>
                        )}

                        {/* Referring Source Filters */}
                        {derivedFilterOptions.referringSources.length > 0 && !isFilterActive('referringSource') && (
                            <>
                                <ListSubheader>{FILTER_TYPE_LABELS.referringSource}</ListSubheader>
                                {derivedFilterOptions.referringSources.map(value => (
                                    <MenuItem
                                        key={`referringSource-${value}`}
                                        onClick={() => handleFilterSelect('referringSource', value, formatLabel(value))}
                                        sx={{ pl: 4 }}
                                    >
                                        {formatLabel(value)}
                                    </MenuItem>
                                ))}
                            </>
                        )}

                        {/* Urgency Filters */}
                        {derivedFilterOptions.urgencies.length > 0 && !isFilterActive('urgency') && (
                            <>
                                <ListSubheader>{FILTER_TYPE_LABELS.urgency}</ListSubheader>
                                {derivedFilterOptions.urgencies.map(value => (
                                    <MenuItem
                                        key={`urgency-${value}`}
                                        onClick={() => handleFilterSelect('urgency', value, formatLabel(value))}
                                        sx={{ pl: 4 }}
                                    >
                                        {formatLabel(value)}
                                    </MenuItem>
                                ))}
                            </>
                        )}

                        {/* Service Type Filters */}
                        {derivedFilterOptions.serviceTypes.length > 0 && !isFilterActive('serviceType') && (
                            <>
                                <ListSubheader>{FILTER_TYPE_LABELS.serviceType}</ListSubheader>
                                {derivedFilterOptions.serviceTypes.map(value => (
                                    <MenuItem
                                        key={`serviceType-${value}`}
                                        onClick={() => handleFilterSelect('serviceType', value, formatLabel(value))}
                                        sx={{ pl: 4 }}
                                    >
                                        {formatLabel(value)}
                                    </MenuItem>
                                ))}
                            </>
                        )}

                        {/* Job Type Filters */}
                        {derivedFilterOptions.jobTypes.length > 0 && !isFilterActive('jobType') && (
                            <>
                                <ListSubheader>{FILTER_TYPE_LABELS.jobType}</ListSubheader>
                                {derivedFilterOptions.jobTypes.slice(0, MAX_JOB_TYPES_DISPLAYED).map(value => (
                                    <MenuItem
                                        key={`jobType-${value}`}
                                        onClick={() => handleFilterSelect('jobType', value, formatLabel(value))}
                                        sx={{ pl: 4 }}
                                    >
                                        {formatLabel(value)}
                                    </MenuItem>
                                ))}
                            </>
                        )}

                        {/* High Value Lead Filter */}
                        {!isFilterActive('highValueLead') && (
                            <>
                                <ListSubheader>{FILTER_TYPE_LABELS.highValueLead}</ListSubheader>
                                <MenuItem
                                    onClick={() => handleFilterSelect('highValueLead', 'true', 'High Value')}
                                    sx={{ pl: 4 }}
                                >
                                    High Value
                                </MenuItem>
                                <MenuItem
                                    onClick={() => handleFilterSelect('highValueLead', 'false', 'Regular')}
                                    sx={{ pl: 4 }}
                                >
                                    Regular
                                </MenuItem>
                            </>
                        )}
                    </Menu>
                </>
            )}

            {/* Organization Filter Chips */}
            {showOrganizationFilter && selectedOrganizationIds.map((orgId) => {
                const org = availableOrganizations.find(o => o.organizationId === orgId);
                return (
                    <Chip
                        key={`org-${orgId}`}
                        label={`Org: ${org?.name || orgId}`}
                        onDelete={onOrganizationFilterChange ? () => {
                            onOrganizationFilterChange(selectedOrganizationIds.filter(id => id !== orgId));
                        } : undefined}
                        deleteIcon={<ClearIcon />}
                        size="small"
                        color="secondary"
                        variant="outlined"
                        sx={{
                            borderRadius: '16px',
                            '& .MuiChip-deleteIcon': {
                                fontSize: '16px'
                            }
                        }}
                    />
                );
            })}

            {/* Active Filter Chips */}
            {activeFilterChips.map((chip) => (
                <Chip
                    key={chip.filterType}
                    label={`${chip.label}: ${chip.value}`}
                    onDelete={onFilterClear ? () => onFilterClear(chip.filterType) : undefined}
                    deleteIcon={<ClearIcon />}
                    size="small"
                    color="primary"
                    variant="outlined"
                    sx={{
                        borderRadius: '16px',
                        '& .MuiChip-deleteIcon': {
                            fontSize: '16px'
                        }
                    }}
                />
            ))}

            {/* View Lead Details */}
            {onViewLeads && hasFilterTheLeadsListCanHonor && (
                <Chip
                    label="View Lead Details"
                    icon={<OpenInNewIcon sx={{ fontSize: '14px !important' }} />}
                    onClick={onViewLeads}
                    size="small"
                    color="primary"
                    variant="filled"
                    clickable
                    sx={{
                        borderRadius: '16px',
                        '& .MuiChip-icon': {
                            marginLeft: '8px',
                            marginRight: '-4px'
                        }
                    }}
                />
            )}

            {/* Clear All Button */}
            {onClearAllFilters && (activeFilterChips.length >= 1 || selectedOrganizationIds.length > 0) && (
                <Chip
                    label="Clear All"
                    onClick={onClearAllFilters}
                    size="small"
                    color="secondary"
                    variant="outlined"
                    sx={{
                        borderRadius: '16px',
                        marginLeft: 'auto'
                    }}
                />
            )}
        </div>
    );
};
